import {
  Component,
  ViewChild,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { GoalsChartComponent } from '../goals-chart/goals-chart.component';
import { Goals } from '../models/goals';
import { User } from '../models/user';
import { AuthService } from '../services/authService';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, debounceTime, Observable, of, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { UserMacroGoalsEntryModel } from '../models/userMacroGoalsEntryModel';
import { validate, ValidationError } from 'class-validator';

@Component({
    selector: 'app-goals',
    templateUrl: './goals.component.html',
    styleUrl: './goals.component.scss',
    standalone: false
})
export class GoalsComponent implements AfterViewInit {
  user = new User('', false);
  userMacroGoalsEntryModel = new UserMacroGoalsEntryModel(0, 0, 0, 0, 0);
  errors: ValidationError[] = [];
  errorMessages: string[] = [];
  @ViewChild(GoalsChartComponent) goalsChartComponent!: GoalsChartComponent;
  @Input() goals = new Goals(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
  @Output() goalsChange = new EventEmitter<Goals>();
  @ViewChild('carbs') carbs!: NgModel;
  @ViewChild('fat') fat!: NgModel;
  @ViewChild('protein') protein!: NgModel;
  @ViewChild('alcohol') alcohol!: NgModel;
  @ViewChild('alcohol') totalCalories!: NgModel;
  response: any;
  renderer: any;

  private inputSubject = new Subject<any>();

  constructor(private authService: AuthService, private http: HttpClient, public el: ElementRef, renderer: Renderer2, public router: Router) {
    this.renderer = renderer;

    // Subscribe to the inputSubject with a debounce time
    this.inputSubject.pipe(debounceTime(2000)).subscribe(event => {
      this.updateChartData(event);
    });
  }

  public async ngOnInit(): Promise<void> {
    const isLoggedIn = await this.checkIfUserIsLoggedIn();
  }

  public checkIfUserIsLoggedIn(): Promise<boolean> {
    return new Promise((resolve) => {
      this.authService.isLoggedIn().subscribe((response: any) => {
        this.user.loggedin = response.isAuthenticated;
        this.user.username = response.userName ?? '';
        resolve(this.user.loggedin);
      });
    });
  }

  public ngAfterViewInit(): void {
    this.getUserMacroGoalsForCurrentUser();
  }

  public onGoalsChange(goals: Goals): void {
    this.goals = new Goals(
      goals.carbs,
      goals.fat,
      goals.protein,
      goals.alcohol,
      goals.carbsPercentage,
      goals.fatPercentage,
      goals.proteinPercentage,
      goals.alcoholPercentage,
      goals.carbCalories,
      goals.fatCalories,
      goals.proteinCalories,
      goals.alcoholCalories,
      goals.totalCalories
    );
    this.goalsChange.emit(this.goals); // Propagate changes up
  }

  public onInput(event: any): void {
    this.inputSubject.next(event);
  }

  public async updateChartData(event?: any): Promise<void> {
    switch (event?.target?.name) {
      case 'carbsGoal':
        this.goals.carbs = event?.target?.valueAsNumber;
        break;
      case 'fatGoal':
        this.goals.fat = event?.target?.valueAsNumber;
        break;
      case 'proteinGoal':
        this.goals.protein = event?.target?.valueAsNumber;
        break;
      case 'alcoholGoal':
        this.goals.alcohol = event?.target?.valueAsNumber;
        break;
    }

    this.calculateCaloriesPerMacro();
    this.calculateCaloriePercentages();
    this.updateChart();

    const userMacroGoalIsValid = await this.validateUserMacroGoalsEntry();
    if (!userMacroGoalIsValid) {
      return;
    }

    await this.saveUserMacoGoals();
    this.goalsChange.emit(this.goals); // Ensure this is emitted after goals are updated
  }

  public updateChart(): void {
    if (this.goalsChartComponent.chart) {
      this.goalsChartComponent.chart.data.datasets[0].data = [
        this.goals.carbsPercentage,
        this.goals.fatPercentage,
        this.goals.proteinPercentage,
        this.goals.alcoholPercentage,
      ];
      this.goalsChartComponent.chart.update(
        this.goals.carbsPercentage,
        this.goals.fatPercentage,
        this.goals.proteinPercentage,
        this.goals.alcoholPercentage
      );
    }
  }

  public calculateCaloriesPerMacro(): void {
    const numberOfCaloriesPerGramOfFat = 9;
    const numberOfCaloriesPerGramOfProtein = 4;
    const numberOfCaloriesPerGramOfCarbohydrate = 4;
    const numberOfCaloriesPerGramOfAlcohol = 7;

    this.goals.carbCalories = this.goals.carbs
      ? this.goals.carbs * numberOfCaloriesPerGramOfCarbohydrate
      : 0;
    this.goals.fatCalories = this.goals.fat
      ? this.goals.fat * numberOfCaloriesPerGramOfFat
      : 0;
    this.goals.proteinCalories = this.goals.protein
      ? this.goals.protein * numberOfCaloriesPerGramOfProtein
      : 0;
    this.goals.alcoholCalories = this.goals.alcohol
      ? this.goals.alcohol * numberOfCaloriesPerGramOfAlcohol
      : 0;
  }

  public calculateCaloriePercentages(): void {
    this.goals.totalCalories =
      this.goals.carbCalories +
      this.goals.fatCalories +
      this.goals.proteinCalories +
      this.goals.alcoholCalories;
    if (this.goals.totalCalories > 0) {
      this.goals.carbsPercentage = Math.round(
        (this.goals.carbCalories / this.goals.totalCalories) * 100
      );
      this.goals.fatPercentage = Math.round(
        (this.goals.fatCalories / this.goals.totalCalories) * 100
      );
      this.goals.proteinPercentage = Math.round(
        (this.goals.proteinCalories / this.goals.totalCalories) * 100
      );
      this.goals.alcoholPercentage = Math.round(
        (this.goals.alcoholCalories / this.goals.totalCalories) * 100
      );
    }
  }

  public async validateUserMacroGoalsEntry(): Promise<boolean> {
    let isValid = false;
    this.errorMessages = [];
    if (this.goals && this.userMacroGoalsEntryModel) {
      this.userMacroGoalsEntryModel.carbs = this.goals.carbs;
      this.userMacroGoalsEntryModel.fat = this.goals.fat;
      this.userMacroGoalsEntryModel.protein = this.goals.protein;
      this.userMacroGoalsEntryModel.alcohol = this.goals.alcohol;
      this.userMacroGoalsEntryModel.calories = this.goals.totalCalories;
      this.errors = await validate(this.userMacroGoalsEntryModel);
      if (this.errors.length <= 0) {
        isValid = true;
      } else {
        for (let error = 0; error < this.errors.length; error++) {
          const constraints = this.errors[error].constraints;
          if (constraints && Object.keys(constraints).length > 0) {
            for (const key in constraints) {
              if (constraints.hasOwnProperty(key)) {
                console.error(constraints[key]);
                this.errorMessages.push(constraints[key]);
              }
            }
          }
        }
      }
    }

    const formElement = this.el.nativeElement.querySelector(
      'form[name="goalsForm"]'
    );

    if (formElement) {
      this.renderer.addClass(formElement, 'was-validated');
    }

    return isValid;
  }

  public async saveUserMacoGoals(): Promise<void> {
    const formElement = this.el.nativeElement.querySelector(
      'form[name="saveUserMacoGoalsForm"]'
    );
    if (formElement) {
      this.renderer.removeClass(formElement, 'needs-validation');
    }
    const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
    this.http
      .post('/api/UserMacroGoals/save_user_macro_goals', this.userMacroGoalsEntryModel, {
        headers: headers,
      })
      .pipe(catchError(this.handleError<any>('saveUserMacoGoals')))
      .subscribe({
        next: (response) => {
          this.response = response;
          if (!response || Object.keys(response).length === 0) {
            console.error('response is undefined or empty');
          } else if (response?.success == false) {
            for (
              let errorMessage = 0;
              errorMessage < response.errorMessages.length;
              errorMessage++
            ) {
              console.error(response.errorMessages[errorMessage]);
              const errorMsgLowerCase = response.errorMessages[errorMessage].toLowerCase().trim();
              if (errorMsgLowerCase.indexOf('exception') != -1) {
                throw new Error(response.errorMessages[errorMessage]);
              }

              if (errorMsgLowerCase.indexOf('user id not found.') != -1 || errorMsgLowerCase.indexOf('user is not authenticated.') != -1) {
                this.router.navigate(['/login']);
              }
            }
          } else {
            // success
            const formElement = this.el.nativeElement.querySelector('form[name="goalsForm"]');
            if (formElement) {
              this.renderer.removeClass(formElement, 'was-validated');
              this.renderer.addClass(formElement, 'needs-validation');
            }
            // emit the 'goalsChanged' event
            this.goalsChange.emit(this.goals);
          }
        },
        error: (error) => {
          this.response = error;
          console.error('Error received:', error.message);
        },
      });
  }

  public handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return of(result as T);
    };
  }

  public async validate(): Promise<ValidationError[]> {
    return await validate(this.userMacroGoalsEntryModel);
  }

  public getObjectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  public getUserMacroGoalsForCurrentUser(): void {
    const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
    this.http
      .get<any>('/api/UserMacroGoals/get_user_macro_goals', {
        headers: headers,
      })
      .pipe(catchError(this.handleError<any>('ngOnInit')))
      .subscribe({
        next: (result) => {
          if (result.success) {
            this.goals.carbs = result.userMacroGoalsEntryModel.carbs;
            this.goals.fat = result.userMacroGoalsEntryModel.fat;
            this.goals.protein = result.userMacroGoalsEntryModel.protein;
            this.goals.alcohol = result.userMacroGoalsEntryModel.alcohol;
            this.goals.totalCalories = result.userMacroGoalsEntryModel.calories;
            this.calculateCaloriesPerMacro();
            this.calculateCaloriePercentages();
            this.updateChart();
          } else {
            console.error('API call was not successful:', result);
          }
        },
        error: (error) => {
          console.error('Error received:', error);
          console.error('Full error response:', error.error);
        },
      });
  }
}
